import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

export default function PartnersSection() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('partners-section');
    if (section) {
      observer.observe(section);
    }

    return () => observer.disconnect();
  }, []);

  const partners = [
    {
      name: 'Orange Money',
      icon: 'ri-smartphone-line',
      color: 'from-orange-500 to-orange-600',
      description: t('orangeDesc', 'خدمة دفع موثوقة وسريعة')
    },
    {
      name: 'Moov Money',
      icon: 'ri-wallet-3-line',
      color: 'from-blue-500 to-blue-600',
      description: t('moovDesc', 'حلول دفع مرنة ومبتكرة')
    },
    {
      name: 'MTN Mobile Money',
      icon: 'ri-bank-card-line',
      color: 'from-yellow-500 to-yellow-600',
      description: t('mtnDesc', 'دفع إلكتروني آمن ومضمون')
    },
    {
      name: 'Wave',
      icon: 'ri-exchange-dollar-line',
      color: 'from-purple-500 to-purple-600',
      description: t('waveDesc', 'تحويلات فورية بدون رسوم')
    },
    {
      name: 'Mikrotik',
      icon: 'ri-router-line',
      color: 'from-red-500 to-red-600',
      description: t('mikrotikDesc', 'حلول شبكات احترافية')
    },
    {
      name: 'Mikhmon',
      icon: 'ri-dashboard-line',
      color: 'from-green-500 to-green-600',
      description: t('mikhmonDesc', 'إدارة متقدمة للشبكات')
    }
  ];

  const testimonials = [
    {
      name: t('customer1Name', 'أحمد محمد'),
      role: t('customer1Role', 'مستخدم منذ سنتين'),
      comment: t('customer1Comment', 'خدمة ممتازة وسريعة، الدفع سهل جداً عبر Orange Money. أنصح الجميع بهذه المنصة!'),
      rating: 5,
      avatar: 'https://public.readdy.ai/ai/img_res/8437dee98dc87a5f9c675b591b43dc28.jpg'
    },
    {
      name: t('customer2Name', 'فاطمة علي'),
      role: t('customer2Role', 'عميلة جديدة'),
      comment: t('customer2Comment', 'أخيراً وجدت خدمة إنترنت موثوقة في منطقتنا. الأسعار معقولة والدعم الفني رائع.'),
      rating: 5,
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20middle%20eastern%20woman%20smiling%20portrait%20wearing%20hijab%20with%20modern%20business%20attire%20against%20clean%20white%20background%20with%20natural%20lighting%20representing%20satisfied%20customer%20testimonial%20for%20technology%20service&width=200&height=200&seq=avatar-002&orientation=squarish'
    },
    {
      name: t('customer3Name', 'عمر حسن'),
      role: t('customer3Role', 'مستخدم منذ 6 أشهر'),
      comment: t('customer3Comment', 'السرعة ممتازة والتفعيل فوري. تجربة رائعة من البداية للنهاية.'),
      rating: 5,
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20young%20middle%20eastern%20man%20smiling%20portrait%20with%20casual%20modern%20clothing%20against%20clean%20white%20background%20with%20natural%20lighting%20representing%20satisfied%20customer%20testimonial%20for%20technology%20service&width=200&height=200&seq=avatar-003&orientation=squarish'
    }
  ];

  return (
    <div id="partners-section" className="py-24 px-6 bg-white relative overflow-hidden">
      <div className="absolute top-20 right-0 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-green-100 rounded-full blur-3xl opacity-50"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
              {t('ourPartners', 'شركاؤنا')}
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            {t('partnersTitle', 'نعمل مع الأفضل')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('partnersDesc', 'شراكات استراتيجية مع أفضل مزودي خدمات الدفع والتكنولوجيا')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {partners.map((partner, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border-2 border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${
                isVisible ? 'animate-slide-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`w-20 h-20 bg-gradient-to-br ${partner.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                <i className={`${partner.icon} text-4xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{partner.name}</h3>
              <p className="text-gray-600 leading-relaxed">{partner.description}</p>
              <div className="mt-6 flex items-center gap-2 text-blue-600 font-semibold cursor-pointer hover:gap-3 transition-all">
                <span>{t('learnMore', 'اعرف المزيد')}</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-green-600 rounded-3xl p-12 shadow-2xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('customerReviews', 'آراء عملائنا')}
            </h3>
            <p className="text-white/90 text-lg">
              {t('reviewsDesc', 'اكتشف تجارب عملائنا الراضين')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover border-4 border-blue-100"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">{testimonial.name}</h4>
                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
                
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                
                <p className="text-gray-700 leading-relaxed italic">
                  "{testimonial.comment}"
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-full px-12 py-6 shadow-lg">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-1">100%</div>
              <div className="text-gray-600 text-sm font-medium">{t('satisfaction', 'رضا العملاء')}</div>
            </div>
            <div className="w-px h-12 bg-gray-300"></div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-1">24/7</div>
              <div className="text-gray-600 text-sm font-medium">{t('support', 'دعم فني')}</div>
            </div>
            <div className="w-px h-12 bg-gray-300"></div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-1">99.9%</div>
              <div className="text-gray-600 text-sm font-medium">{t('reliability', 'موثوقية')}</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
